"use client";
import React, { useState, useEffect } from "react";
import { Box, IconButton, Tooltip, Zoom, Badge } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useLanguage } from "./LanguageProvider";

export const FloatingButtons = () => {
  const { currentLang } = useLanguage();
  const isRTL = currentLang === "ar";
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Animation variants for container
  const containerVariants = {
    hidden: { 
      opacity: 0,
      scale: 0,
      x: isRTL ? -50 : 50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Animation variants for individual buttons
  const buttonVariants = {
    hidden: { 
      opacity: 0,
      scale: 0,
      x: isRTL ? -30 : 30
    },
    visible: { 
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  };

  // Pulse animation for notification
  const pulseAnimation = {
    scale: [1, 1.1, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Ring animation for phone
  const ringAnimation = {
    rotate: [0, 15, -15, 15, -15, 0],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
      repeatDelay: 2
    }
  };

  // Shake animation for WhatsApp
  const shakeAnimation = {
    x: [0, -3, 3, -3, 3, 0],
    transition: {
      duration: 0.8,
      repeat: Infinity,
      ease: "easeInOut",
      repeatDelay: 3
    }
  };

  // Bounce animation for Scroll Top button
  const bounceAnimation = {
    y: [0, -5, 0, 5, 0],
    transition: {
      duration: 1.2,
      repeat: Infinity,
      ease: "easeInOut",
      repeatDelay: 2
    }
  };

  // Handle scroll to show scroll top button only
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Show scroll top button when scrolled down more than 300px
      setShowScrollTop(currentScrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto show buttons with animation on load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleCall = () => {
    window.location.href = "tel:+971503330841";
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/971503330841", "_blank");
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 20, sm: 30 },
        [isRTL ? "left" : "right"]: { xs: 15, sm: 20 },
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {/* Scroll to Top Button - يظهر فقط عند التمرير لأسفل */}
            {showScrollTop && (
              <Tooltip
                title={currentLang === "ar" ? "أعلى الصفحة" : "Scroll to Top"}
                placement={isRTL ? "right" : "left"}
                TransitionComponent={Zoom}
                arrow
              >
                <motion.div
                  variants={buttonVariants}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton
                    onClick={handleScrollToTop}
                    sx={{
                      bgcolor: "#c9a03d",
                      color: "#1a1a2e",
                      width: { xs: 50, sm: 55 },
                      height: { xs: 50, sm: 55 },
                      "&:hover": {
                        bgcolor: "#a07d2e",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(201, 160, 61, 0.4)",
                    }}
                  >
                    <motion.div animate={bounceAnimation}>
                      <ArrowUpwardIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    </motion.div>
                  </IconButton>
                </motion.div>
              </Tooltip>
            )}

            {/* WhatsApp Button */}
            <Tooltip
              title={currentLang === "ar" ? "واتساب" : "WhatsApp"}
              placement={isRTL ? "right" : "left"}
              TransitionComponent={Zoom}
              arrow
            >
              <motion.div
                variants={buttonVariants}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: isRTL ? "left" : "right",
                  }}
                  badgeContent={
                    <motion.div
                      animate={pulseAnimation}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#4CAF50",
                        border: "2px solid white",
                      }}
                    />
                  }
                >
                  <IconButton
                    onClick={handleWhatsApp}
                    sx={{
                      bgcolor: "#25D366",
                      color: "white",
                      width: { xs: 50, sm: 55 },
                      height: { xs: 50, sm: 55 },
                      "&:hover": {
                        bgcolor: "#128C7E",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(37, 211, 102, 0.4)",
                    }}
                  >
                    <motion.div animate={shakeAnimation}>
                      <WhatsAppIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    </motion.div>
                  </IconButton>
                </Badge>
              </motion.div>
            </Tooltip>

            {/* Phone Button */}
            <Tooltip
              title={currentLang === "ar" ? "اتصال" : "Call"}
              placement={isRTL ? "right" : "left"}
              TransitionComponent={Zoom}
              arrow
            >
              <motion.div
                variants={buttonVariants}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: isRTL ? "left" : "right",
                  }}
                  badgeContent={
                    <motion.div
                      animate={pulseAnimation}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#f44336",
                        border: "2px solid white",
                      }}
                    />
                  }
                >
                  <IconButton
                    onClick={handleCall}
                    sx={{
                      bgcolor: "#1a1a2e",
                      color: "white",
                      width: { xs: 50, sm: 55 },
                      height: { xs: 50, sm: 55 },
                      "&:hover": {
                        bgcolor: "#c9a03d",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    <motion.div animate={ringAnimation}>
                      <PhoneIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    </motion.div>
                  </IconButton>
                </Badge>
              </motion.div>
            </Tooltip>
          </motion.div>
        )}
      </AnimatePresence>

     

      {/* Ripple effect behind buttons */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          backgroundColor: "#c9a03d",
          zIndex: -1,
          opacity: 0.2,
        }}
      />
    </Box>
  );
};

export default FloatingButtons;